import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Feature } from '@kb3d/models/classes/features/feature';
import { ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { SyncService } from '@kb3d/models/services/sync-service';
import { Renderer } from '../renderer';
import { FeatureGeometryProcessor } from './geometry-processor/base-processor';
import type { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { IWorkerRenderFeaturesData } from '@kb3d/models/interfaces/worker-types';

export type SubmeshVertexDataMap = Map<string, SubmeshVertexData>;

export abstract class FeatureRenderer<T extends Feature, WorkerPayload = any> extends Renderer<T, Mesh> {
    // vertexDataCache = new Map<string, Partial<SubmeshVertexData>>();

    protected declare abstract processor: FeatureGeometryProcessor<T>;

    /** @deprecated */
    updateMeshGeometry(feature: T, vertexData: SubmeshVertexData, vertexDataChanges: Partial<SubmeshVertexData>) {
        const meshDynamicId = feature._parent?._dynamicId || '';
        return this.processor.updateMeshGeometry(feature, vertexData, vertexDataChanges, { meshDynamicId });
    }

    /** @deprecated */
    updateMeshGeometryCache(feature: T, vertexData: SubmeshVertexData, vertexDataChanges: Partial<SubmeshVertexData>) {
        return this.processor.updateMeshGeometryCache(feature, vertexData, vertexDataChanges);
    }

    /** @deprecated */
    clearCache(features?: Feature[]) {
        this.processor.clearCache(features);
    }

    makeWorkerPayload(
        feature: T,
        _geometryData: SubmeshVertexDataMap
    ): IWorkerRenderFeaturesData<WorkerPayload> | Promise<IWorkerRenderFeaturesData<WorkerPayload>> {
        const serializedFeature = feature._manager?.serialize(feature);
        if (!serializedFeature) {
            throw Error('Feature not able to be serialized');
        }
        return {
            feature: serializedFeature,
            featureData: null as any,
        };
    }

    postUpdateGeometry?(_node: ITrackedClass<T>, _vertexData: SubmeshVertexData): void;

    // public updateMeshGeometryCache(
    //     feature: T,
    //     vertexData: SubmeshVertexData,
    //     vertexDataChanges: Partial<SubmeshVertexData>
    // ): Partial<SubmeshVertexData> {
    //     const self = this;
    //     const newVertexData: Partial<SubmeshVertexData> = {};

    //     // Grab the properties in the mesh vertex that are needed for the feature
    //     // Only swap out the properties in the old vertex data with the stored copy if they have NOT been affected by another vertex
    //     if (feature.affectedBy) {
    //         const cachedData = this.vertexDataCache.get(feature._dynamicId);
    //         feature.affectedBy.forEach(affectedBy => {
    //             if (affectedBy === eMeshChangeTypes.VertexIndices) {
    //                 if (vertexDataChanges.indices === null) {
    //                     newVertexData.indices = null;
    //                     clearVertexDataCache('indices');
    //                 } else if (vertexDataChanges.indices) {
    //                     newVertexData.indices = vertexDataChanges.indices.slice();
    //                     updateVertexDataCache('indices', vertexDataChanges.indices);
    //                 } else if (cachedData) {
    //                     newVertexData.indices = cachedData.indices?.slice() as any;
    //                 } else {
    //                     newVertexData.indices = vertexData.indices!.slice();
    //                     updateVertexDataCache('indices', vertexData.indices);
    //                 }

    //                 if (vertexDataChanges.paths === null) {
    //                     newVertexData.paths = null;
    //                     clearVertexDataCache('paths');
    //                 } else if (vertexDataChanges.paths) {
    //                     newVertexData.paths = vertexDataChanges.paths.slice();
    //                     updateVertexDataCache('paths', vertexDataChanges.paths);
    //                 } else if (cachedData) {
    //                     newVertexData.paths = cachedData.paths?.slice() as any;
    //                 } else {
    //                     if (vertexData.paths) {
    //                         newVertexData.paths = vertexData.paths.slice();
    //                         updateVertexDataCache('paths', vertexData.paths);
    //                     } else {
    //                         newVertexData.paths = null;
    //                     }
    //                 }

    //                 if (vertexDataChanges.holes === null) {
    //                     newVertexData.holes = null;
    //                     clearVertexDataCache('holes');
    //                 } else if (vertexDataChanges.holes) {
    //                     newVertexData.holes = vertexDataChanges.holes.slice();
    //                     updateVertexDataCache('holes', vertexDataChanges.holes);
    //                 } else if (cachedData) {
    //                     newVertexData.holes = cachedData.holes?.slice() as any;
    //                 } else {
    //                     if (vertexData.holes) {
    //                         newVertexData.holes = vertexData.holes.slice();
    //                         updateVertexDataCache('holes', vertexData.holes);
    //                     } else {
    //                         newVertexData.holes = null;
    //                     }
    //                 }
    //             }
    //             if (affectedBy === eMeshChangeTypes.VertexPositions) {
    //                 if (vertexDataChanges.positions === null) {
    //                     newVertexData.positions = null;
    //                     clearVertexDataCache('positions');
    //                 } else if (vertexDataChanges.positions) {
    //                     newVertexData.positions = vertexDataChanges.positions.slice();
    //                     updateVertexDataCache('positions', vertexDataChanges.positions);
    //                 } else if (cachedData) {
    //                     newVertexData.positions = cachedData.positions?.slice() as any; // compiler complains about the slice generic type for some reason
    //                 } else {
    //                     if (!vertexData.positions) {
    //                         throw Error('Invalid geometry: positions is null');
    //                     }
    //                     newVertexData.positions = vertexData.positions.slice();
    //                     updateVertexDataCache('positions', vertexData.positions);
    //                 }
    //             }
    //             if (affectedBy === eMeshChangeTypes.VertexNormals) {
    //                 if (vertexDataChanges.normals === null) {
    //                     newVertexData.normals = null;
    //                     clearVertexDataCache('normals');
    //                 } else if (vertexDataChanges.normals) {
    //                     newVertexData.normals = vertexDataChanges.normals.slice();
    //                     updateVertexDataCache('normals', vertexDataChanges.normals);
    //                 } else if (cachedData) {
    //                     newVertexData.normals = cachedData.normals?.slice() as any;
    //                 } else {
    //                     if (vertexData.normals) {
    //                         newVertexData.normals = vertexData.normals.slice();
    //                         updateVertexDataCache('normals', vertexData.normals);
    //                     } else {
    //                         newVertexData.normals = null;
    //                     }
    //                 }
    //             }
    //             if (affectedBy === eMeshChangeTypes.VertexUvs) {
    //                 if (vertexDataChanges.uvs === null) {
    //                     newVertexData.uvs = null;
    //                     clearVertexDataCache('uvs');
    //                 } else if (vertexDataChanges.uvs) {
    //                     newVertexData.uvs = vertexDataChanges.uvs.slice();
    //                     updateVertexDataCache('uvs', vertexDataChanges.uvs);
    //                 } else if (cachedData) {
    //                     newVertexData.uvs = cachedData.uvs?.slice() as any;
    //                 } else {
    //                     if (vertexData.uvs) {
    //                         newVertexData.uvs = vertexData.uvs.slice();
    //                         updateVertexDataCache('uvs', vertexData.uvs);
    //                     } else {
    //                         newVertexData.uvs = null;
    //                     }
    //                 }
    //             }
    //             if (affectedBy === eMeshChangeTypes.Submeshes) {
    //                 if (vertexDataChanges.submeshData === null) {
    //                     newVertexData.submeshData = null;
    //                     clearVertexDataCache('submeshData');
    //                 } else if (vertexDataChanges.submeshData) {
    //                     newVertexData.submeshData = JSON.parse(JSON.stringify(vertexDataChanges.submeshData));
    //                     updateVertexDataCache('submeshData', JSON.parse(JSON.stringify(vertexDataChanges.submeshData)));
    //                 } else if (cachedData) {
    //                     const cachedSubmeshes = cachedData.submeshData;
    //                     newVertexData.submeshData = cachedSubmeshes
    //                         ? JSON.parse(JSON.stringify(cachedSubmeshes))
    //                         : undefined;
    //                 } else {
    //                     if (vertexData.submeshData) {
    //                         newVertexData.submeshData = JSON.parse(JSON.stringify(vertexData.submeshData));
    //                         updateVertexDataCache('submeshData', JSON.parse(JSON.stringify(vertexData.submeshData)));
    //                     } else {
    //                         newVertexData.submeshData = null;
    //                     }
    //                 }
    //             }
    //         });
    //     }

    //     function clearVertexDataCache(prop: keyof SubmeshVertexData) {
    //         const vertexDataCache = self.vertexDataCache.get(feature._dynamicId);
    //         if (vertexDataCache) {
    //             delete vertexDataCache[prop];
    //         }
    //     }

    //     function updateVertexDataCache(prop: keyof SubmeshVertexData, value: any) {
    //         if (!value) {
    //             throw Error('invalid geometry does not have ' + prop);
    //         }

    //         let vertexDataCache = self.vertexDataCache.get(feature._dynamicId);
    //         if (!vertexDataCache) {
    //             vertexDataCache = {};
    //             self.vertexDataCache.set(feature._dynamicId, vertexDataCache);
    //         }

    //         if (typeof value.slice !== 'function') {
    //             throw Error('fixme: how do I clone this property?');
    //         }
    //         vertexDataCache[prop] = value.slice(); // Need to make sure everything is cloned when inserted into the cache
    //     }

    //     return newVertexData;
    // }

    // public updateMeshGeometry(
    //     feature: T,
    //     vertexData: SubmeshVertexData,
    //     vertexDataChanges: Partial<SubmeshVertexData>
    // ): Partial<SubmeshVertexData> {
    //     return this.updateMeshGeometryCache(feature, vertexData, vertexDataChanges);
    // }

    public delete3d(node: ITrackedClass<T>, preserveCache?: boolean) {
        if (!preserveCache) {
            this.processor.clearCache([node]);
        }
        const t3d = this.get3d(node);
        if (t3d) {
            super.delete3d(node);
        } else {
            SyncService.nodeDeletionProcessed(node);
        }
    }

    public dispose() {
        super.dispose();
        this.processor.clearCache();
    }
}
